import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import apiClient from '../utils/apiClient';
const extractLeaderboard = (data) =>
  Array.isArray(data) ? data : (data.leaderboard || []);
function AdminPanel() {
  const { user } = useContext(AuthContext);
  const [allRaces, setAllRaces] = useState([]);
  const [races, setRaces] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [raceScores, setRaceScores] = useState([]);
  const [selectedRace, setSelectedRace] = useState(null);
  const [selectedScore, setSelectedScore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('results');
  const [raceResult, setRaceResult] = useState(null);
  const [calculatingScores, setCalculatingScores] = useState(false);
  const [processingLatestRace, setProcessingLatestRace] = useState(false);
  const [calculateScoreResult, setCalculateScoreResult] = useState(null);
  const [forceRecalculate, setForceRecalculate] = useState({});
  const [drivers, setDrivers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [editingRace, setEditingRace] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState(new Date().getFullYear().toString());
  const [availableSeasons, setAvailableSeasons] = useState([]);
  const [headToHeadConfig, setHeadToHeadConfig] = useState({
    drivers: {
      driver1: { driverId: '', code: '', name: '' },
      driver2: { driverId: '', code: '', name: '' }
    },
    teams: {
      team1: { constructorId: '', name: '' },
      team2: { constructorId: '', name: '' }
    },
    sprintDrivers: {
      driver1: { driverId: '', code: '', name: '' },
      driver2: { driverId: '', code: '', name: '' }
    },
    sprintTeams: {
      team1: { constructorId: '', name: '' },
      team2: { constructorId: '', name: '' }
    }
  });
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const allRacesResponse = await apiClient.get(`/api/f1data/races/all`);
        const allRacesData = allRacesResponse.data;
        setAllRaces(allRacesData);
        const seasons = [...new Set(allRacesData.map(r => String(r.season)))].sort().reverse();
        setAvailableSeasons(seasons);
        const filteredRaces = allRacesData.filter(race => String(race.season) === selectedSeason);
        setRaces(filteredRaces);
        const leaderboardResponse = await apiClient.get(`/api/scores/leaderboard`);
        setLeaderboard(extractLeaderboard(leaderboardResponse.data));
        const driversResponse = await apiClient.get(`/api/f1data/drivers?season=${selectedSeason}`);
        setDrivers(driversResponse.data);
        const teamsResponse = await apiClient.get(`/api/f1data/teams?season=${selectedSeason}`);
        setTeams(teamsResponse.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedSeason]);
  function transformRaceResult(result) {
    if (!result) return result;
    const transformed = { ...result };
    if (result.headToHead && result.headToHead.drivers) {
      transformed.driverHeadToHead = {
        driver1: result.headToHead.drivers.driver1?.name || result.headToHead.drivers.driver1?.driverId,
        driver2: result.headToHead.drivers.driver2?.name || result.headToHead.drivers.driver2?.driverId,
        winner: result.headToHead.drivers.winner
      };
    }
    if (result.headToHead && result.headToHead.teams) {
      transformed.teamHeadToHead = {
        team1: result.headToHead.teams.team1?.name || result.headToHead.teams.team1?.constructorId,
        team2: result.headToHead.teams.team2?.name || result.headToHead.teams.team2?.constructorId,
        winner: result.headToHead.teams.winner
      };
    }
    return transformed;
  }
  const processResults = async (raceId, raceName, forceRecalc = false) => {
    try {
      setLoading(true);
      setMessage('');
      setError('');
      const url = forceRecalc
        ? `/api/results/process/${raceId}?force=true`
        : `/api/results/process/${raceId}`;
      const response = await apiClient.post(url);
      const msg = forceRecalc
        ? `Scores RECALCULATED for ${raceName}. ${response.data.scores} predictions updated.`
        : `Results processed for ${raceName}. ${response.data.scores} predictions scored.`;
      setMessage(msg);
      if (selectedRace === raceName) await fetchRaceScores(raceId, raceName);
      const leaderboardResponse = await apiClient.get(`/api/scores/leaderboard`);
      setLeaderboard(extractLeaderboard(leaderboardResponse.data));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to process results');
    } finally {
      setLoading(false);
    }
  };
  const getLatestCompletedRace = () => {
    const now = new Date();
    return [...races]
      .filter(race => race?.date && new Date(race.date) <= now)
      .sort((a, b) => new Date(b.date) - new Date(a.date))[0] || null;
  };
const processLatestRaceAndUpdateScores = async () => {
  const latestRace = getLatestCompletedRace();

  if (!latestRace) {
    setError(`No completed races found for ${selectedSeason}`);
    return;
  }

  try {
    setProcessingLatestRace(true);
    setMessage('');
    setError('');
    setCalculateScoreResult(null);

    // Process and force-score the latest race.
    // Force makes this safe to rerun if the race result was already imported.
    const processResponse = await apiClient.post(
      `/api/results/process/${latestRace._id}?force=true`
    );

    // Recalculate overall user totals / leaderboard.
    const scoreResponse = await apiClient.post(
      `/api/scores/calculate-all-scores`
    );

    setCalculateScoreResult(scoreResponse.data);

    // Refresh leaderboard data.
    const leaderboardResponse = await apiClient.get(
      `/api/scores/leaderboard`
    );

    setLeaderboard(
      extractLeaderboard(leaderboardResponse.data)
    );

    setMessage(
      `${latestRace.raceName} processed successfully. ` +
      `${processResponse.data?.scores ?? 0} predictions scored and leaderboard updated.`
    );
  } catch (err) {
    console.error(
      'Error processing latest race and updating scores:',
      err
    );

    setError(
      err.response?.data?.message ||
      'Failed to process the latest race and update scores'
    );
  } finally {
    setProcessingLatestRace(false);
  }
};
  const fetchRaceScores = async (raceId, raceName) => {
    if (!raceId) { setError('Cannot fetch scores: Race ID is missing'); return; }
    try {
      setLoading(true);
      setError('');
      const response = await apiClient.get(`/api/scores/race/${raceId}`);
      if (response.data && response.data.scores) {
        setRaceScores(response.data.scores);
        setRaceResult(transformRaceResult(response.data.result));
        setSelectedRace(raceName);
        setActiveTab('raceScores');
      } else {
        setError('Received invalid data format from server');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load race scores');
    } finally {
      setLoading(false);
    }
  };
  const calculateAllScores = async () => {
    try {
      setCalculatingScores(true);
      setError('');
      setMessage('');
      setCalculateScoreResult(null);
      const response = await apiClient.post(`/api/scores/calculate-all-scores`);
      setCalculateScoreResult(response.data);
      setMessage('All user scores calculated and leaderboard updated successfully!');
      const leaderboardResponse = await apiClient.get(`/api/scores/leaderboard`);
      setLeaderboard(extractLeaderboard(leaderboardResponse.data));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to calculate user scores');
    } finally {
      setCalculatingScores(false);
    }
  };
  const editHeadToHead = async (race) => {
    setEditingRace(race);
    const defaultConfig = {
      drivers: { driver1: { driverId: '', code: '', name: '' }, driver2: { driverId: '', code: '', name: '' } },
      teams: { team1: { constructorId: '', name: '' }, team2: { constructorId: '', name: '' } },
      sprintDrivers: { driver1: { driverId: '', code: '', name: '' }, driver2: { driverId: '', code: '', name: '' } },
      sprintTeams: { team1: { constructorId: '', name: '' }, team2: { constructorId: '', name: '' } }
    };
    if (race.headToHead) {
      setHeadToHeadConfig({
        drivers: race.headToHead.drivers || defaultConfig.drivers,
        teams: race.headToHead.teams || defaultConfig.teams,
        sprintDrivers: race.headToHead.sprint?.drivers || defaultConfig.sprintDrivers,
        sprintTeams: race.headToHead.sprint?.teams || defaultConfig.sprintTeams
      });
    } else {
      setHeadToHeadConfig(defaultConfig);
    }
    setActiveTab('headToHead');
  };
  const handleDriverSelect = (position, driverId, raceType = 'feature') => {
    const selectedDriver = drivers.find(d => d.driverId === driverId);
    if (selectedDriver) {
      const driverKey = raceType === 'sprint' ? 'sprintDrivers' : 'drivers';
      setHeadToHeadConfig({
        ...headToHeadConfig,
        [driverKey]: {
          ...headToHeadConfig[driverKey],
          [position]: {
            driverId: selectedDriver.driverId,
            code: selectedDriver.code,
            name: `${selectedDriver.givenName} ${selectedDriver.familyName}`
          }
        }
      });
    }
  };
  const handleTeamSelect = (position, constructorId, raceType = 'feature') => {
    const selectedTeam = teams.find(t => t.constructorId === constructorId);
    if (selectedTeam) {
      const teamKey = raceType === 'sprint' ? 'sprintTeams' : 'teams';
      setHeadToHeadConfig({
        ...headToHeadConfig,
        [teamKey]: {
          ...headToHeadConfig[teamKey],
          [position]: { constructorId: selectedTeam.constructorId, name: selectedTeam.name }
        }
      });
    }
  };
  const saveHeadToHead = async () => {
    if (!editingRace) return;
    try {
      setLoading(true);
      setError('');
      setMessage('');
      if (!headToHeadConfig.drivers.driver1.driverId || !headToHeadConfig.drivers.driver2.driverId ||
          !headToHeadConfig.teams.team1.constructorId || !headToHeadConfig.teams.team2.constructorId) {
        setError('Please select all drivers and teams for feature race head-to-head');
        setLoading(false); return;
      }
      if (headToHeadConfig.drivers.driver1.driverId === headToHeadConfig.drivers.driver2.driverId) {
        setError('Please select different drivers for feature race head-to-head');
        setLoading(false); return;
      }
      if (headToHeadConfig.teams.team1.constructorId === headToHeadConfig.teams.team2.constructorId) {
        setError('Please select different teams for feature race head-to-head');
        setLoading(false); return;
      }
      if (editingRace.isSprint) {
        if (!headToHeadConfig?.sprintDrivers?.driver1?.driverId || !headToHeadConfig?.sprintDrivers?.driver2?.driverId ||
            !headToHeadConfig?.sprintTeams?.team1?.constructorId || !headToHeadConfig?.sprintTeams?.team2?.constructorId) {
          setError('Please select all drivers and teams for sprint race head-to-head');
          setLoading(false); return;
        }
        if (headToHeadConfig?.sprintDrivers?.driver1?.driverId === headToHeadConfig?.sprintDrivers?.driver2?.driverId) {
          setError('Please select different drivers for sprint race head-to-head');
          setLoading(false); return;
        }
        if (headToHeadConfig?.sprintTeams?.team1?.constructorId === headToHeadConfig?.sprintTeams?.team2?.constructorId) {
          setError('Please select different teams for sprint race head-to-head');
          setLoading(false); return;
        }
      }
      const d1 = drivers.find(d => d.driverId === headToHeadConfig.drivers.driver1.driverId);
      const d2 = drivers.find(d => d.driverId === headToHeadConfig.drivers.driver2.driverId);
      const t1 = teams.find(t => t.constructorId === headToHeadConfig.teams.team1.constructorId);
      const t2 = teams.find(t => t.constructorId === headToHeadConfig.teams.team2.constructorId);
      const completeConfig = {
        feature: {
          drivers: {
            driver1: { driverId: d1.driverId, code: d1.code, name: `${d1.givenName} ${d1.familyName}`, _id: d1._id },
            driver2: { driverId: d2.driverId, code: d2.code, name: `${d2.givenName} ${d2.familyName}`, _id: d2._id }
          },
          teams: {
            team1: { constructorId: t1.constructorId, name: t1.name, _id: t1._id },
            team2: { constructorId: t2.constructorId, name: t2.name, _id: t2._id }
          }
        }
      };
      if (editingRace.isSprint) {
        const sd1 = drivers.find(d => d.driverId === headToHeadConfig?.sprintDrivers?.driver1?.driverId);
        const sd2 = drivers.find(d => d.driverId === headToHeadConfig?.sprintDrivers?.driver2?.driverId);
        const st1 = teams.find(t => t.constructorId === headToHeadConfig?.sprintTeams?.team1?.constructorId);
        const st2 = teams.find(t => t.constructorId === headToHeadConfig?.sprintTeams?.team2?.constructorId);
        if (sd1 && sd2 && st1 && st2) {
          completeConfig.sprint = {
            drivers: {
              driver1: { driverId: sd1.driverId, code: sd1.code, name: `${sd1.givenName} ${sd1.familyName}`, _id: sd1._id },
              driver2: { driverId: sd2.driverId, code: sd2.code, name: `${sd2.givenName} ${sd2.familyName}`, _id: sd2._id }
            },
            teams: {
              team1: { constructorId: st1.constructorId, name: st1.name, _id: st1._id },
              team2: { constructorId: st2.constructorId, name: st2.name, _id: st2._id }
            }
          };
        }
      }
      const response = await apiClient.patch(`/api/f1data/races/${editingRace._id}`, { headToHead: completeConfig });
      if (response.data) {
        setRaces(races.map(r => r._id === editingRace._id ? { ...r, headToHead: completeConfig } : r));
        setAllRaces(allRaces.map(r => r._id === editingRace._id ? { ...r, headToHead: completeConfig } : r));
        setHeadToHeadConfig(completeConfig);
        setMessage('Head-to-head configuration saved successfully');
        setActiveTab('results');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save head-to-head configuration');
    } finally {
      setLoading(false);
    }
  };
  const viewScoreDetails = (score) => {
    setSelectedScore(score);
    setActiveTab('scoreDetails');
  };
  const renderScoreBreakdown = (breakdown) => {
    if (!breakdown || breakdown.length === 0) return <p>No breakdown available</p>;
    const byType = {};
    breakdown.forEach(item => {
      if (!byType[item.type]) byType[item.type] = [];
      byType[item.type].push(item);
    });
    return (
      <div className="space-y-3">
        {Object.entries(byType).map(([type, items]) => {
          const totalForType = items.reduce((sum, item) => sum + item.points, 0);
          return (
            <div key={type} className="p-3 bg-gray-50 rounded">
              <div className="flex justify-between items-center">
                <h4 className="font-medium">{type.replace(/_/g, ' ')}:</h4>
                <span className="font-bold text-blue-600">+{totalForType} pts</span>
              </div>
              <div className="mt-1">
                {items.map((item, i) => (
                  <div key={i} className="text-sm text-gray-700 flex justify-between">
                    <span>{item.driver}</span>
                    <span>+{item.points} pt{item.points !== 1 ? 's' : ''}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };
  const renderCompleteBreakdown = (score, result) => {
    const scoreBreakdown = score.scoreBreakdown || [];
    const featureEntry = scoreBreakdown.find(e => e.type === 'feature');
    const featurePreds = score.prediction?.featurePredictions || score.prediction?.predictions;
    if (!featurePreds) return <p className="text-gray-500">No prediction data available.</p>;
    const getScoredItem = (labelPrefix) => featureEntry?.breakdown?.find(b => b.item?.startsWith(labelPrefix));
    const toCode = (driverId) => {
      if (!driverId) return '—';
      const found = result.results?.find(r => r.driverId === driverId);
      if (found?.code) return found.code;
      return driverId.replace(/_/g, ' ').substring(0, 3).toUpperCase();
    };
    const getActualPos = (driverId) => {
      if (!driverId) return null;
      const found = result.results?.find(r => r.driverId === driverId);
      return found ? parseInt(found.position) : null;
    };
    const podium = featurePreds.podium || [];
    const positionRows = podium.map((driverId, idx) => {
      const posLabel = `P${idx + 1}`;
      const scoredItem = getScoredItem(posLabel);
      const actualPos = getActualPos(driverId);
      const isCorrect = scoredItem?.item?.includes('correct');
      const points = scoredItem?.total || 0;
      const isInTop5 = actualPos !== null && (actualPos <= 5 || points > 0);
      let indicator = '✗'; let indicatorClass = 'text-red-500 font-bold';
      if (isCorrect) { indicator = '✓'; indicatorClass = 'text-green-600 font-bold'; }
      else if (isInTop5) { indicator = '~'; indicatorClass = 'text-yellow-500 font-bold'; }
      return { category: posLabel, predicted: toCode(driverId), actual: actualPos ? `P${actualPos}` : 'DNF', indicator, indicatorClass, points };
    });
    const bonusItem = getScoredItem('All podium bonus') || getScoredItem('All 5 bonus');
    const polePred = featurePreds.polePosition; const poleActual = result.polePosition;
    const poleCorrect = polePred && poleActual && polePred === poleActual;
    const fastPred = featurePreds.fastestLap; const fastActual = result.fastestLap;
    const fastCorrect = fastPred && fastActual && fastPred === fastActual;
    const dnfPred = featurePreds.firstRetirement; const dnfActual = result.firstRetirement;
    const dnfCorrect = dnfPred && dnfActual && dnfPred === dnfActual;
    const drH2HPred = featurePreds.driverHeadToHead?.winner;
    const drH2HActual = result.headToHead?.drivers?.winner;
    const drH2HCorrect = drH2HPred && drH2HActual && (drH2HPred === drH2HActual || toCode(drH2HActual) === drH2HPred);
    const drH2HName1 = result.headToHead?.drivers?.driver1?.code || '?';
    const drH2HName2 = result.headToHead?.drivers?.driver2?.code || '?';
    const tmH2HPred = featurePreds.teamHeadToHead?.winner;
    const tmH2HActual = result.headToHead?.teams?.winner;
    const tmH2HCorrect = (getScoredItem('Team H2H correct')?.total || 0) > 0;
    const tm1Name = result.headToHead?.teams?.team1?.name || '?';
    const tm2Name = result.headToHead?.teams?.team2?.name || '?';
    const mpgActual = result.mpg;
    const mpgCorrect = mpgActual && featurePreds.mostPlacesGained && featurePreds.mostPlacesGained === mpgActual;
    const otherRows = [
      { category: 'Pole', predicted: toCode(polePred), actual: poleActual ? toCode(poleActual) : '—', indicator: poleCorrect ? '✓' : '✗', indicatorClass: poleCorrect ? 'text-green-600 font-bold' : 'text-red-500 font-bold', points: getScoredItem('Pole correct')?.total || 0 },
      { category: 'Fastest Lap', predicted: toCode(fastPred), actual: fastActual ? toCode(fastActual) : 'TBC', indicator: !fastActual ? '—' : fastCorrect ? '✓' : '✗', indicatorClass: !fastActual ? 'text-gray-400' : fastCorrect ? 'text-green-600 font-bold' : 'text-red-500 font-bold', points: getScoredItem('FastLap correct')?.total || 0 },
      { category: 'DNF', predicted: toCode(dnfPred), actual: dnfActual ? toCode(dnfActual) : '—', indicator: dnfCorrect ? '✓' : '✗', indicatorClass: dnfCorrect ? 'text-green-600 font-bold' : 'text-red-500 font-bold', points: getScoredItem('First retirement correct')?.total || 0 },
      { category: 'MPG', predicted: toCode(featurePreds.mostPlacesGained), actual: mpgActual ? toCode(mpgActual) : 'TBC', indicator: !mpgActual ? '—' : mpgCorrect ? '✓' : '✗', indicatorClass: !mpgActual ? 'text-gray-400' : mpgCorrect ? 'text-green-600 font-bold' : 'text-red-500 font-bold', points: getScoredItem('MPG correct')?.total || 0 },
      { category: `DR H2H (${drH2HName1} vs ${drH2HName2})`, predicted: toCode(drH2HPred), actual: drH2HActual ? toCode(drH2HActual) : '—', indicator: drH2HCorrect ? '✓' : '✗', indicatorClass: drH2HCorrect ? 'text-green-600 font-bold' : 'text-red-500 font-bold', points: getScoredItem('Driver H2H correct')?.total || 0 },
      { category: `TM H2H (${tm1Name} vs ${tm2Name})`, predicted: tmH2HPred || '—', actual: tmH2HActual || '—', indicator: tmH2HCorrect ? '✓' : '✗', indicatorClass: tmH2HCorrect ? 'text-green-600 font-bold' : 'text-red-500 font-bold', points: getScoredItem('Team H2H correct')?.total || 0 },
    ];
    const renderRow = (row, idx) => (
      <tr key={idx} className={row.points > 0 ? 'bg-green-50' : ''}>
        <td className="px-3 py-2 text-sm font-medium text-gray-700">{row.category}</td>
        <td className="px-3 py-2 text-sm text-black font-mono font-semibold">{row.predicted}</td>
        <td className="px-3 py-2 text-sm text-gray-500 font-mono">{row.actual}</td>
        <td className={`px-3 py-2 text-center text-base ${row.indicatorClass}`}>{row.indicator}</td>
        <td className="px-3 py-2 text-right text-sm font-semibold text-black">{row.points > 0 ? `+${row.points}` : '—'}</td>
      </tr>
    );
    return (
      <div className="space-y-4">
        <div className="border rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Predicted</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actual</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase w-12"></th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Pts</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {positionRows.map((row, idx) => renderRow(row, idx))}
              <tr className="bg-gray-50"><td colSpan="5" className="px-3 py-1 text-xs text-gray-400 uppercase tracking-wide">Other Predictions</td></tr>
              {otherRows.map((row, idx) => renderRow(row, idx + positionRows.length))}
              {bonusItem && (
                <>
                  <tr className="bg-gray-50"><td colSpan="5" className="px-3 py-1 text-xs text-gray-400 uppercase tracking-wide">Bonus Points</td></tr>
                  <tr className="bg-green-50">
                    <td className="px-3 py-2 text-sm font-medium text-gray-700">{bonusItem.item}</td>
                    <td className="px-3 py-2 text-sm text-gray-400" colSpan="2">All positions correct</td>
                    <td className="px-3 py-2 text-center text-base text-green-600 font-bold">✓</td>
                    <td className="px-3 py-2 text-right text-sm font-semibold text-black">+{bonusItem.total}</td>
                  </tr>
                </>
              )}
            </tbody>
            <tfoot className="bg-blue-50 border-t-2 border-blue-200">
              <tr>
                <td colSpan="4" className="px-3 py-3 text-base font-semibold text-blue-800">Total Score</td>
                <td className="px-3 py-3 text-right text-base font-bold text-blue-800">{score.score} pts</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    );
  };
  // Access guard — must be after all hooks
  if (!user?.isAdmin) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-900 text-red-200 px-4 py-3 rounded">Access denied.</div>
      </div>
    );
  }
  if (loading && !races.length) return <div className="text-center py-8">Loading data...</div>;
  const latestCompletedRace = getLatestCompletedRace();
  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-bold mb-4">Admin Panel</h2>
      {message && <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">{message}</div>}
      {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">{error}</div>}
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h3 className="font-semibold text-gray-900">Latest Race</h3>
            {latestCompletedRace ? (
              <>
                <p className="text-sm text-gray-700 mt-1">
                  <span className="font-medium">{latestCompletedRace.raceName}</span>
                  {' - '}
                  {new Date(latestCompletedRace.date).toLocaleDateString('en-AU')}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  This will process the race results, recalculate all user scores, and refresh the leaderboard.
                </p>
              </>
            ) : (
              <p className="text-sm text-gray-500 mt-1">
                No completed races found for {selectedSeason}.
              </p>
            )}
          </div>
          <button
            onClick={processLatestRaceAndUpdateScores}
            disabled={!latestCompletedRace || processingLatestRace || loading}
            className="bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {processingLatestRace ? 'Processing...' : 'Process & Update Scores'}
          </button>
        </div>
      </div>
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Season:</label>
        <select value={selectedSeason} onChange={(e) => setSelectedSeason(e.target.value)} className="w-full max-w-xs px-4 py-2 border rounded bg-white text-gray-900">
          {availableSeasons.map(season => <option key={season} value={season}>{season}</option>)}
        </select>
        <p className="text-xs text-gray-500 mt-2">Currently showing {races.length} races for {selectedSeason}</p>
      </div>
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-8">
          <button className={`py-4 px-1 ${activeTab === 'results' ? 'border-b-2 border-blue-500 font-medium text-blue-600' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('results')}>Process Results</button>
          {selectedRace && <button className={`py-4 px-1 ${activeTab === 'raceScores' ? 'border-b-2 border-blue-500 font-medium text-blue-600' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('raceScores')}>{selectedRace} Scores</button>}
          {selectedScore && <button className={`py-4 px-1 ${activeTab === 'scoreDetails' ? 'border-b-2 border-blue-500 font-medium text-blue-600' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('scoreDetails')}>Score Details</button>}
          {editingRace && <button className={`py-4 px-1 ${activeTab === 'headToHead' ? 'border-b-2 border-blue-500 font-medium text-blue-600' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('headToHead')}>Head-to-Head Setup</button>}
        </nav>
      </div>
      {activeTab === 'results' && (
        <div>
          <h3 className="text-lg font-semibold mb-3">Process Race Results</h3>
          <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <h4 className="font-medium text-yellow-800 mb-2">Scoring Options</h4>
            <label className="flex items-center">
              <input type="checkbox"
                checked={Object.keys(forceRecalculate).length > 0 && Object.values(forceRecalculate).every(v => v)}
                onChange={(e) => {
                  if (e.target.checked) {
                    const newForceRecalc = {};
                    races.forEach(race => { newForceRecalc[race._id] = true; });
                    setForceRecalculate(newForceRecalc);
                  } else {
                    setForceRecalculate({});
                  }
                }}
                className="mr-2"
              />
              <span className="text-sm text-yellow-800"><strong>Force Recalculate All Races</strong></span>
            </label>
            <p className="text-xs text-yellow-700 ml-6">⚠️ This will overwrite all existing scores</p>
          </div>
          <div className="space-y-4">
            {races.map(race => (
              <div key={race._id} className="border p-3 rounded">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-black">{race.raceName} {race.isSprint ? '(Sprint Weekend)' : ''}</h4>
                    <p className="text-sm text-gray-500">{new Date(race.date).toLocaleDateString('en-AU')}{race.circuit?.country && ` - ${race.circuit.country}`}</p>
                    {race.headToHead && (
                      <div className="mt-1 text-xs text-gray-600">
                        <p><span className="font-medium">Feature DR H2H:</span> {race.headToHead.feature?.drivers?.driver1?.name || race.headToHead.drivers?.driver1?.name || 'Not set'} vs {race.headToHead.feature?.drivers?.driver2?.name || race.headToHead.drivers?.driver2?.name || 'Not set'}</p>
                        <p><span className="font-medium">Feature TM H2H:</span> {race.headToHead.feature?.teams?.team1?.name || race.headToHead.teams?.team1?.name || 'Not set'} vs {race.headToHead.feature?.teams?.team2?.name || race.headToHead.teams?.team2?.name || 'Not set'}</p>
                        {race.isSprint && <p><span className="font-medium">Sprint DR H2H:</span> {race.headToHead.sprint?.drivers?.driver1?.name || 'Not set'} vs {race.headToHead.sprint?.drivers?.driver2?.name || 'Not set'}</p>}
                        {race.isSprint && <p><span className="font-medium">Sprint TM H2H:</span> {race.headToHead.sprint?.teams?.team1?.name || 'Not set'} vs {race.headToHead.sprint?.teams?.team2?.name || 'Not set'}</p>}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <button onClick={() => editHeadToHead(race)} className="text-blue-600 hover:text-blue-800 text-sm px-3 py-1 border border-blue-500 rounded">Set H2H</button>
                    <button onClick={() => fetchRaceScores(race._id, race.raceName)} className="text-green-600 hover:text-green-800 text-sm px-3 py-1 border border-green-500 rounded">View Scores</button>
                              <button onClick={() => processResults(race._id, race.raceName, forceRecalculate[race._id] || false)} className={`text-sm px-3 py-1 border rounded ${forceRecalculate[race._id] ? 'text-orange-600 hover:text-orange-800 border-orange-500 bg-orange-50' : 'text-purple-600 hover:text-purple-800 border-purple-500'}`} disabled={loading}>
                      {forceRecalculate[race._id] ? 'Force Recalc' : 'Process'}
                    </button>
                  </div>
                </div>
                <div className="mt-2 ml-4">
                  <label className="flex items-center text-sm">
                    <input type="checkbox" checked={forceRecalculate[race._id] || false} onChange={(e) => setForceRecalculate({ ...forceRecalculate, [race._id]: e.target.checked })} className="mr-2" />
                    <span className="text-gray-600">Force recalculate</span>
                  </label>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {activeTab === 'raceScores' && (
        <div>
          <h3 className="text-lg font-semibold mb-3">{selectedRace} Scores</h3>
          {raceScores.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">UserName</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Score</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {raceScores.map((score, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap font-medium text-black">{score.username}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right font-bold text-black">{score.score}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <button onClick={() => viewScoreDetails(score)} className="text-blue-600 hover:text-blue-800 text-sm">View Details</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500">No scores available for this race yet.</p>
          )}
        </div>
      )}
      {activeTab === 'scoreDetails' && selectedScore && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-black">{selectedScore.username}'s Score Breakdown</h3>
            <span className="text-xl font-bold text-blue-600">{selectedScore.score} points</span>
          </div>
          {raceResult ? renderCompleteBreakdown(selectedScore, raceResult) : (
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <h4 className="font-medium text-gray-700 mb-2">Points Earned</h4>
              {selectedScore.breakdown && renderScoreBreakdown(selectedScore.breakdown)}
            </div>
          )}
          <div className="mt-6">
            <button onClick={() => setActiveTab('raceScores')} className="bg-gray-900 text-white py-2 px-4 rounded hover:bg-gray-700">Back to {selectedRace} Scores</button>
          </div>
        </div>
      )}
      {activeTab === 'headToHead' && editingRace && (
        <div>
          <h3 className="text-lg font-semibold mb-3">Configure Head-to-Head for {editingRace.raceName}</h3>
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <h4 className="text-md font-medium mb-4 text-black">Feature Race - Driver Head-to-Head</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-700 font-medium mb-2">Driver 1</label>
                <select value={headToHeadConfig.drivers.driver1.driverId} onChange={(e) => handleDriverSelect('driver1', e.target.value, 'feature')} className="w-full p-2 border rounded">
                  <option value="">Select Driver 1</option>
                  {drivers.map(driver => <option key={`d1-${driver.driverId}`} value={driver.driverId} disabled={driver.driverId === headToHeadConfig.drivers.driver2.driverId}>{driver.givenName} {driver.familyName} ({driver.code})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-2">Driver 2</label>
                <select value={headToHeadConfig.drivers.driver2.driverId} onChange={(e) => handleDriverSelect('driver2', e.target.value, 'feature')} className="w-full p-2 border rounded">
                  <option value="">Select Driver 2</option>
                  {drivers.map(driver => <option key={`d2-${driver.driverId}`} value={driver.driverId} disabled={driver.driverId === headToHeadConfig.drivers.driver1.driverId}>{driver.givenName} {driver.familyName} ({driver.code})</option>)}
                </select>
              </div>
            </div>
          </div>
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <h4 className="text-md font-medium mb-4 text-black">Feature Race - Team Head-to-Head</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-700 font-medium mb-2">Team 1</label>
                <select value={headToHeadConfig.teams.team1.constructorId} onChange={(e) => handleTeamSelect('team1', e.target.value, 'feature')} className="w-full p-2 border rounded">
                  <option value="">Select Team 1</option>
                  {teams.map(team => <option key={`t1-${team.constructorId}`} value={team.constructorId} disabled={team.constructorId === headToHeadConfig.teams.team2.constructorId}>{team.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-2">Team 2</label>
                <select value={headToHeadConfig.teams.team2.constructorId} onChange={(e) => handleTeamSelect('team2', e.target.value, 'feature')} className="w-full p-2 border rounded">
                  <option value="">Select Team 2</option>
                  {teams.map(team => <option key={`t2-${team.constructorId}`} value={team.constructorId} disabled={team.constructorId === headToHeadConfig.teams.team1.constructorId}>{team.name}</option>)}
                </select>
              </div>
            </div>
          </div>
          {editingRace.isSprint && (
            <>
              <div className="bg-white shadow rounded-lg p-6 mb-6">
                <h4 className="text-md font-medium mb-4 text-black">Sprint Race - Driver Head-to-Head</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Driver 1</label>
                    <select value={headToHeadConfig?.sprintDrivers?.driver1?.driverId || ''} onChange={(e) => handleDriverSelect('driver1', e.target.value, 'sprint')} className="w-full p-2 border rounded">
                      <option value="">Select Driver 1</option>
                      {drivers.map(driver => <option key={`sd1-${driver.driverId}`} value={driver.driverId} disabled={driver.driverId === (headToHeadConfig?.sprintDrivers?.driver2?.driverId || '')}>{driver.givenName} {driver.familyName} ({driver.code})</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Driver 2</label>
                    <select value={headToHeadConfig?.sprintDrivers?.driver2?.driverId || ''} onChange={(e) => handleDriverSelect('driver2', e.target.value, 'sprint')} className="w-full p-2 border rounded">
                      <option value="">Select Driver 2</option>
                      {drivers.map(driver => <option key={`sd2-${driver.driverId}`} value={driver.driverId} disabled={driver.driverId === (headToHeadConfig?.sprintDrivers?.driver1?.driverId || '')}>{driver.givenName} {driver.familyName} ({driver.code})</option>)}
                    </select>
                  </div>
                </div>
              </div>
              <div className="bg-white shadow rounded-lg p-6 mb-6">
                <h4 className="text-md font-medium mb-4 text-black">Sprint Race - Team Head-to-Head</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Team 1</label>
                    <select value={headToHeadConfig?.sprintTeams?.team1?.constructorId || ''} onChange={(e) => handleTeamSelect('team1', e.target.value, 'sprint')} className="w-full p-2 border rounded">
                      <option value="">Select Team 1</option>
                      {teams.map(team => <option key={`st1-${team.constructorId}`} value={team.constructorId} disabled={team.constructorId === (headToHeadConfig?.sprintTeams?.team2?.constructorId || '')}>{team.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Team 2</label>
                    <select value={headToHeadConfig?.sprintTeams?.team2?.constructorId || ''} onChange={(e) => handleTeamSelect('team2', e.target.value, 'sprint')} className="w-full p-2 border rounded">
                      <option value="">Select Team 2</option>
                      {teams.map(team => <option key={`st2-${team.constructorId}`} value={team.constructorId} disabled={team.constructorId === (headToHeadConfig?.sprintTeams?.team1?.constructorId || '')}>{team.name}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            </>
          )}
          <div className="flex justify-between mt-6">
            <button onClick={() => { setEditingRace(null); setActiveTab('results'); }} className="bg-gray-200 text-gray-800 py-2 px-4 rounded hover:bg-gray-300">Cancel</button>
            <button onClick={saveHeadToHead} className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700" disabled={loading}>{loading ? 'Saving...' : 'Save Head-to-Head Configuration'}</button>
          </div>
        </div>
      )}
    </div>
  );
}
export default AdminPanel;